import { MetricsWithCustomAttribution } from 'models/CampaignMetric'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { FetchableMetricPropsT, fetchableMetrics } from './helpers'

const FetchableMetricSection: React.FC<FetchableMetricPropsT> = props => {
  const {
    children,
    shouldFetch,
    className = '',
    reFetchAt = null,
    ...typeProps
  } = props
  const dispatch = useDispatch()

  // Keep typeProps in a ref so the fetch effect can read current values
  // without needing the object itself as a dependency (which would
  // re-fire the effect every render since rest-spread creates a new ref).
  // isCustomAutomated is intentionally left in typeProps (not destructured
  // separately) so it's also read from the ref at fetch time.
  const typePropsRef = useRef(typeProps)
  typePropsRef.current = typeProps

  // allows some changes to cause data to refetch
  const refetchDependencies = (() => {
    switch (typeProps.type) {
      case 'campaign_metric':
        return typeProps.metricTypes.some(metricType =>
          MetricsWithCustomAttribution.includes(metricType)
        )
          ? [typeProps.attributionWindow]
          : []
      case 'campaign_event':
        return [
          typeProps.dateRangeFilter.start,
          typeProps.dateRangeFilter.end,
          typeProps.attributionWindow,
        ]
      case 'points_experience':
      case 'purchases_metric':
        return [typeProps.dateRangeFilter.start, typeProps.dateRangeFilter.end]
      case 'lifecycle_time_series_metric':
        return [typeProps.timespan]
    }

    return [reFetchAt]
  })()

  const [hasFetched, setHasFetched] = useState(false)

  useEffect(() => {
    if (shouldFetch) {
      setHasFetched(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, ...refetchDependencies])

  useEffect(() => {
    async function fetchData() {
      const currentProps = typePropsRef.current
      await fetchableMetrics[currentProps.type]({
        dispatch,
        ...currentProps,
      })
    }
    if (shouldFetch && !hasFetched) {
      fetchData()
      setHasFetched(true)
    }
    // typeProps intentionally read from ref — only shouldFetch/hasFetched
    // should trigger this effect. refetchDependencies (above) handles
    // resetting hasFetched when relevant props change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, shouldFetch, hasFetched])

  return <div className={className}>{children}</div>
}

export default FetchableMetricSection
